import { type IBTGridExpanableCellProps } from "commonComponents/utilities/Grid/common/BTGridExpandableCell";
import { BTUnimplementedCell } from "commonComponents/utilities/Grid/common/BTUnimplementedCell";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    type GridColumn,
    type IGridRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";

interface IGridCardCellRendererProps {
    column: GridColumn<unknown, unknown>;
    row: IGridRowData;
    rowIndex: number;
}

const noop = () => {};

/**
 * Renders one row+column outside Kendo's `<td>` context. Builds the same
 * `IGridCellRenderProps` shape that BTGridCell builds, with formik handlers
 * stubbed (cards are read-only). For cells whose column has
 * `isExpandable === "insideCell"`, we pass `isColumnExpanded: true` so the
 * cell renders its full content (the card itself owns expansion now).
 */
export const GridCardCellRenderer: React.FC<IGridCardCellRendererProps> = ({
    column,
    row,
    rowIndex,
}) => {
    const rawValue = row[column.jsonKey];
    // `column.mapper` is typed against the column's CellJSON generic, but we hold
    // the column as `GridColumn<unknown, unknown>` here, so the mapper signature
    // collapses to `(unknown) => unknown` at the call site.
    const mapper = column.mapper as ((value: unknown) => unknown) | undefined;
    const cellValue = (mapper ? mapper(rawValue) : rawValue) ?? null;

    const cellProps: IGridCellRenderProps<unknown> = {
        cellValue,
        column,
        entityId: row.id,
        isInEdit: false,
        canEdit: false,
        isDisabled: false,
        field: {
            path: `rowData[${rowIndex}]`,
            name: column.jsonKey,
            full: `rowData[${rowIndex}].${column.jsonKey}`,
        },
        rowIndex,
        onChange: noop,
        onBlur: noop,
        size: "small",
    };

    if (!column.Cell) {
        return <BTUnimplementedCell column={column} cellValue={cellValue} />;
    }

    if (column.isExpandable === "insideCell") {
        const ExpandableCell = column.Cell as React.ComponentType<
            IGridCellRenderProps<unknown> & IBTGridExpanableCellProps
        >;
        return (
            <ExpandableCell
                {...cellProps}
                isColumnExpanded
                onExpandToggle={noop}
                fieldName={column.jsonKey}
                entityId={row.id}
            />
        );
    }

    const Cell = column.Cell;
    return <Cell {...cellProps} />;
};
